// googleMeetUtils.js - Google Meet REST API utilities (not Calendar API)
import { auth } from './firebase';
import api from './utils/api';

/**
 * Create a formatter for dates used in Google Meet space creation
 * @param {Date|string} dateObj - The date object or string
 * @param {string} timeString - Time string like "1:00 PM" or "12:30 AM"
 * @returns {string} ISO string formatted for Google Meet
 */
const formatDateForGoogleMeet = (dateObj, timeString) => {
  const date = new Date(dateObj);
  
  // Parse time string like "1:00 PM" or "12:30 AM"
  const [timePart, ampm] = timeString.split(' ');
  const [hours, minutes] = timePart.split(':').map(Number);
  
  // Convert to 24-hour format
  let hour24 = hours;
  if (ampm.toUpperCase() === 'PM' && hours < 12) {
    hour24 += 12;
  } else if (ampm.toUpperCase() === 'AM' && hours === 12) {
    hour24 = 0;
  }
  
  // Set the time on the date object
  date.setHours(hour24, minutes, 0, 0);
    // Return ISO string formatted for Google Meet
  return date.toISOString();
};
/*
 * 
 * @param {Object} sessionDetails - Details about the tutoring session
 * @param {string} sessionDetails.bookingId - The booking ID for tracking
 * @param {Date|string} sessionDetails.date - The session date
 * @param {string} sessionDetails.startTime - Start time (e.g., "1:00 PM")
 * @param {string} sessionDetails.endTime - End time (e.g., "2:00 PM")
 * @param {string} sessionDetails.tutorName - The name of the tutor
 * @param {string} sessionDetails.studentName - The name of the student
 * @param {string} sessionDetails.tutorEmail - The tutor's email address
 * @param {string} sessionDetails.studentEmail - The student's email address
 * @param {string} sessionDetails.text - Additional details about the session
 * @returns {Promise<string|null>} - The Google Meet link or null if creation failed
 */
export const createGoogleMeetLink = async (sessionDetails) => {
  try {
    // Verify user authentication for security
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return null;
    }    // Create the Meet space using Meet REST API
    const response = await api.post('/api/createGoogleMeetEvent', {
        email: currentUser.email, // Add email for protected route verification
        bookingId: sessionDetails.bookingId,
        summary: `Bright-Bridge Tutoring: ${sessionDetails.tutorName} & ${sessionDetails.studentName}`,
        description: `${sessionDetails.text || 'Tutoring session'}\n\nBooking ID: ${sessionDetails.bookingId}`,
          // Timing
        startDateTime: formatDateForGoogleMeet(sessionDetails.date, sessionDetails.startTime),
        endDateTime: formatDateForGoogleMeet(sessionDetails.date, sessionDetails.endTime),
        
        // Required validation fields
        tutorName: sessionDetails.tutorName,
        studentName: sessionDetails.studentName,
        tutorEmail: sessionDetails.tutorEmail,
        studentEmail: sessionDetails.studentEmail,
        
        // Participants - server expects 'attendees' array
        attendees: [
          {
            email: sessionDetails.tutorEmail,
            displayName: sessionDetails.tutorName,
            responseStatus: 'accepted'
          },
          {
            email: sessionDetails.studentEmail,
            displayName: sessionDetails.studentName,
            responseStatus: 'accepted'
          }
        ],
        
        // Recording settings
        enableRecording: true
      });
    
    if (!response.ok) {
      const errorData = await response.json();
      
      return null;
    }
      const data = await response.json();
      if (data.success && data.meetLink) {
      
      return data.meetLink;
    } else {
      return null;
    }
    
  } catch (error) {
    console.error('Error creating Google Meet link:', error);
    return null;
  }
};